import PropTypes from 'prop-types';
import React from 'react';

import Badge from '../../Atoms/Badge';
import Card, { CardAppearance } from '../../Atoms/Card';
import Picture from '../../Atoms/Picture';
import Tag from '../../Atoms/Tag';

import { propTypesGatsbyImage } from '../../../shared/constants/Proptypes';
import { truncateString } from '../../../shared/utils/truncateString';

import { DocumentType } from '../../../shared/constants/Enums';
import * as S from './ArticleCard.styles';

export const ArticleCard = ({
  cardAppearance,
  categoryColor,
  categoryName,
  type,
  title,
  illustrationProps,
  isPast,
  toComeLabel,
  pastLabel,
  lede,
  tags,
  uid,
}) => {
  if (!uid || !title || !illustrationProps || !categoryName || !categoryColor) {
    return null;
  }

  const articleTitle = truncateString(title, 120);
  const articleLede = truncateString(lede, 120);

  const isFloating = cardAppearance === CardAppearance.SIMPLE;
  const hasStatus = type === DocumentType.WOOBINAR && toComeLabel && pastLabel;

  if (cardAppearance === CardAppearance.HORIZONTAL) {
    return (
      <S.ArticleCard>
        <Card appearance={cardAppearance}>
          <S.HorizontalFlex>
            <S.LinkBox href={uid}>
              <Picture
                height='100%'
                radius='15px'
                width='150px'
                {...illustrationProps}
              />
            </S.LinkBox>
            <S.HorizontalWrapper>
              <S.BadgeContainer cardAppearance={cardAppearance}>
                <Badge color={categoryColor}>{categoryName}</Badge>
              </S.BadgeContainer>
              <S.LinkBox href={uid}>
                <S.TitleContainer cardAppearance={cardAppearance}>
                  {title && (
                    <S.HorizontalTitle>{articleTitle}</S.HorizontalTitle>
                  )}
                </S.TitleContainer>
              </S.LinkBox>
            </S.HorizontalWrapper>
          </S.HorizontalFlex>
        </Card>
      </S.ArticleCard>
    );
  }

  return (
    <S.ArticleCard>
      <Card appearance={cardAppearance} isFloating={isFloating}>
        <S.LinkBox href={uid}>
          <Picture
            height='180px'
            radius='15px'
            width='100%'
            {...illustrationProps}
          />
        </S.LinkBox>
        <S.BadgeContainer cardAppearance={cardAppearance} hasStatus={hasStatus}>
          <Badge color={categoryColor}>{categoryName}</Badge>
          {hasStatus && (
            <S.StatusWrapper isPast={isPast}>
              {!isPast && (
                <S.StatusLabel>{toComeLabel.toUpperCase()}</S.StatusLabel>
              )}
              {isPast && (
                <S.StatusLabel>{pastLabel.toUpperCase()}</S.StatusLabel>
              )}
            </S.StatusWrapper>
          )}
        </S.BadgeContainer>
        <S.LinkBox href={uid}>
          <S.TitleContainer cardAppearance={cardAppearance}>
            {title && <S.VerticalTitle>{articleTitle}</S.VerticalTitle>}
          </S.TitleContainer>
          <S.LedeContainer>
            {lede && <S.Lede>{articleLede}</S.Lede>}
          </S.LedeContainer>
        </S.LinkBox>
        <S.TagList>
          {tags.map((tag, index) => (
            <S.TagContainer key={`${tag}-${index}`}>
              <Tag link={tag.uid}>{tag.name}</Tag>
            </S.TagContainer>
          ))}
        </S.TagList>
      </Card>
    </S.ArticleCard>
  );
};

ArticleCard.propTypes = {
  /**
   * Set the appareance of the button. Simple with a box-shadow by default.
   */
  cardAppearance: PropTypes.oneOf(Object.values(CardAppearance)),
  /**
   * Color on the category label.
   */
  categoryColor: PropTypes.string.isRequired,
  /**
   * Category label.
   */
  categoryName: PropTypes.string.isRequired,
  /**
   * Article illustration.
   */
  illustrationProps: PropTypes.shape({
    imageObject: PropTypes.shape(propTypesGatsbyImage),
    imageUrl: PropTypes.string,
  }).isRequired,
  /**
   * Indicates if the woobinar is past on the card.
   */
  isPast: PropTypes.bool,
  /**
   * Article description displayed when cardAppearance !== 'horizontal'
   * (120 char max).
   */
  lede: PropTypes.string,
  /**
   * Label displayed when the webinar is past.
   */
  pastLabel: PropTypes.string,
  /**
   * Array of tag displayed when cardAppearance !== 'horizontal'.
   * A tag is made of a tag name and a tag uid.
   */
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      uid: PropTypes.string,
    })
  ),
  /**
   * Article title (55 char max).
   */
  title: PropTypes.string.isRequired,
  /**
   * Label displayed when the woobinar is to come.
   */
  toComeLabel: PropTypes.string,
  /**
   * Indicates the Prismic document type of the page.
   */
  type: PropTypes.oneOf(Object.values(DocumentType)),
  /**
   * Article Prismic uid.
   */
  uid: PropTypes.string.isRequired,
};

ArticleCard.defaultProps = {
  cardAppearance: CardAppearance.SIMPLE,
  illustrationAlt: 'article-picture',
  isPast: false,
};
